import { useEffect, useMemo, useState } from 'react';
import { Layers } from 'lucide-react';
import { useAuthStore } from '@/lib/auth-store';
import {
  apiClient,
  type ActionGroup,
  type ActiveFilter,
  type ApiError,
  type MembershipWithUserResponse,
} from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import {
  Field,
  GlassSelect,
  TextInput,
} from '@/components/clients/form-primitives';
import { ConfigModal } from './config-modal';
import { ListToolbar } from './list-toolbar';

interface FormState {
  name: string;
  responsiblePublicationsUserId: string | null;
  active: boolean;
}

const EMPTY: FormState = {
  name: '',
  responsiblePublicationsUserId: null,
  active: true,
};

export function ActionGroupsPage() {
  const tenantId = useAuthStore((s) => s.tenantId);
  const [items, setItems] = useState<ActionGroup[]>([]);
  const [members, setMembers] = useState<MembershipWithUserResponse[]>([]);
  const [active, setActive] = useState<ActiveFilter>('true');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ActionGroup | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    apiClient
      .tenantsListMembers(tenantId)
      .then((rows) => {
        if (!cancelled) setMembers(rows);
      })
      .catch(() => {
        // membros sao opcionais — silenciar erro
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .actionGroupsList({ active, search: search.trim() || undefined })
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch((e: ApiError) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, search]);

  const memberOptions = useMemo(
    () => [
      { value: '', label: '— Sem responsável —' },
      ...members.map((m) => ({
        value: m.user.id,
        label: m.user.name || m.user.email,
      })),
    ],
    [members],
  );

  function openNew() {
    setEditing(null);
    setForm(EMPTY);
    setFormError(null);
    setModalOpen(true);
  }

  function openEdit(item: ActionGroup) {
    setEditing(item);
    setForm({
      name: item.name,
      responsiblePublicationsUserId: item.responsiblePublicationsUserId,
      active: item.active,
    });
    setFormError(null);
    setModalOpen(true);
  }

  async function submit() {
    if (!form.name.trim()) {
      setFormError('Nome obrigatório');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      if (editing) {
        await apiClient.actionGroupsUpdate(editing.id, {
          name: form.name.trim(),
          responsiblePublicationsUserId:
            form.responsiblePublicationsUserId || null,
          active: form.active,
        });
      } else {
        await apiClient.actionGroupsCreate({
          name: form.name.trim(),
          responsiblePublicationsUserId:
            form.responsiblePublicationsUserId || null,
          active: form.active,
        });
      }
      setModalOpen(false);
      // refetch
      const rows = await apiClient.actionGroupsList({
        active,
        search: search.trim() || undefined,
      });
      setItems(rows);
    } catch (e) {
      setFormError((e as ApiError).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(item: ActionGroup) {
    if (!window.confirm(`Excluir o grupo "${item.name}"?`)) return;
    try {
      await apiClient.actionGroupsDelete(item.id);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
    } catch (e) {
      window.alert((e as ApiError).message);
    }
  }

  function memberName(id: string | null): string {
    if (!id) return '—';
    const m = members.find((x) => x.user.id === id);
    return m ? m.user.name || m.user.email : '—';
  }

  return (
    <div className="space-y-6">
      <ListToolbar
        title="Grupos de ação"
        description="Áreas de atuação do escritório (Cível, Família, Previdenciário, etc)."
        newLabel="Novo grupo"
        onNew={openNew}
        active={active}
        onActiveChange={setActive}
        search={search}
        onSearchChange={setSearch}
      />

      <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]">
        {loading ? (
          <p className="px-4 py-8 text-center text-sm text-[var(--fg-muted)]">
            Carregando…
          </p>
        ) : error ? (
          <p className="px-4 py-8 text-center text-sm text-red-500">{error}</p>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
            <Layers className="h-10 w-10 text-[var(--fg-muted)] opacity-40" />
            <p className="text-sm text-[var(--fg-muted)]">
              Nenhum grupo cadastrado. Clique em <strong>Novo grupo</strong>{' '}
              para começar.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-left text-xs uppercase tracking-wide text-[var(--fg-muted)]">
              <tr>
                <th className="px-4 py-2 font-medium">Nome</th>
                <th className="px-4 py-2 font-medium">Responsável publicações</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="border-t border-[var(--border)] transition hover:bg-[color-mix(in_oklch,var(--fg)_4%,transparent)]"
                >
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      onClick={() => openEdit(item)}
                      className="text-left font-medium text-[var(--fg)] hover:text-[var(--accent)]"
                    >
                      {item.name}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-[var(--fg-muted)]">
                    {memberName(item.responsiblePublicationsUserId)}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={
                        item.active
                          ? 'inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-500'
                          : 'inline-flex items-center rounded-full bg-zinc-500/15 px-2 py-0.5 text-xs font-medium text-zinc-400'
                      }
                    >
                      {item.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(item)}
                    >
                      Excluir
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfigModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar grupo de ação' : 'Novo grupo de ação'}
        description="Área de atuação do escritório."
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setModalOpen(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting ? 'Salvando…' : 'Salvar'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {formError && (
            <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500">
              {formError}
            </p>
          )}
          <Field label="Nome" required>
            <TextInput
              value={form.name}
              onChange={(e) =>
                setForm((s) => ({ ...s, name: e.target.value }))
              }
              placeholder="Ex: Família e Sucessões"
              maxLength={120}
            />
          </Field>
          <Field
            label="Responsável por publicações"
            hint="Usuário que recebe publicações dessa área."
          >
            <GlassSelect
              value={form.responsiblePublicationsUserId ?? ''}
              onChange={(v) =>
                setForm((s) => ({
                  ...s,
                  responsiblePublicationsUserId: v || null,
                }))
              }
              options={memberOptions}
              placeholder="Selecione"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm text-[var(--fg)]">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) =>
                setForm((s) => ({ ...s, active: e.target.checked }))
              }
              className="h-4 w-4 rounded border-[var(--border)]"
            />
            Ativo
          </label>
        </div>
      </ConfigModal>
    </div>
  );
}
